import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TeamLogo from './TeamLogo';
import TeamProfileHub from './TeamProfileHub';
import { getTeams } from '../services/teams';
import { canonicalTeamSlug } from '../data/teamIdentity';
import '../styles/teams.css';
import '../styles/teams-refinements.css';

const FAVORITES_STORAGE_KEY = 'volleywire.favoriteTeams.v1';
const FAVORITES_EVENT = 'volleywire:favorites-changed';
const DEFAULT_TEAM_SEASON = 2026;
const FALLBACK_SEASONS = [2026, 2025, 2024, 2023, 2022, 2021];
const INITIAL_VISIBLE_TEAMS = 48;
const LOAD_MORE_COUNT = 48;

const FILTER_OPTIONS = [
  { value: 'all', label: 'All teams' },
  { value: 'favorites', label: 'My Teams' },
  {
    value: 'ranked',
    label: 'Top 25',
    requiresRankings: true,
  },
];

function normalizeTeamKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');
}

function getFavoriteKey(value) {
  const key = canonicalTeamSlug(value?.slug || value?.name || value);
  return key ? `team:${key}` : '';
}

function getTeamFavoriteKeys(team) {
  const values = [
        team?.name,
        team?.slug,
        team?.longName,
        ...(Array.isArray(team?.aliases) ? team.aliases : []),
      ].filter(Boolean);
  return Array.from(new Set(values.flatMap((value) => {
    const canonical = getFavoriteKey(value);
    const legacy = normalizeTeamKey(value);
    return [canonical, legacy ? `name:${legacy}` : ''];
  }).filter(Boolean)));
}

function readFavoriteTeams() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];

    if (!Array.isArray(parsed)) {
      return [];
    }

    return Array.from(
      new Set(
        parsed
          .filter((value) => typeof value === 'string')
          .map((value) => value.trim())
          .filter(Boolean)
      )
    );
  } catch {
    return [];
  }
}

function saveFavoriteTeams(favorites) {
  try {
    window.localStorage.setItem(
      FAVORITES_STORAGE_KEY,
      JSON.stringify(favorites)
    );

    window.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT, {
        detail: { favorites },
      })
    );
  } catch {
    // Favorites remain available for the current Teams session.
  }
}

function isFavoriteTeam({ team, favorites }) {
  const favoriteSet =
    favorites instanceof Set ? favorites : new Set(favorites);

  return getTeamFavoriteKeys(team).some((key) => favoriteSet.has(key));
}

function normalizeSearchValue(value) {
  return String(value || '').trim().toLowerCase();
}

function matchesTeamSearch(team, query) {
  if (!query) {
    return true;
  }

  const values = [
    team?.name,
    team?.longName,
    team?.record,
    team?.finalRankLabel,
    team?.archivedRankLabel,
    team?.seasonFinishDisplay,
    team?.seasonFinishLabel,
    team?.seasonFinish?.display,
    team?.seasonFinish?.label,
    ...(Array.isArray(team?.aliases) ? team.aliases : []),
  ];

  return values.some((value) =>
    normalizeSearchValue(value).includes(query)
  );
}

function compareTeamNames(left, right) {
  return String(left?.name || '').localeCompare(
    String(right?.name || ''),
    undefined,
    { sensitivity: 'base' }
  );
}

function getSortableWinPct(team) {
  return Number.isFinite(team?.winPct) ? team.winPct : -1;
}

function getSortableWins(team) {
  return Number.isFinite(team?.wins) ? team.wins : -1;
}

function getSortableLosses(team) {
  return Number.isFinite(team?.losses)
    ? team.losses
    : Number.MAX_SAFE_INTEGER;
}

function getFinalRank(team) {
  const values = [team?.finalRank, team?.archivedRank, team?.rank];
  return values.find((value) => Number.isFinite(value)) ?? null;
}

function isFinalRanked(team) {
  return Number.isFinite(getFinalRank(team));
}

function getSortableRank(team) {
  return getFinalRank(team) ?? Number.MAX_SAFE_INTEGER;
}

function getTeamRecordLabel(team) {
  return (
    team?.finalRecord?.label ||
    team?.record ||
    team?.rankingRecord ||
    'Record unavailable'
  );
}

function getSeasonFinishLabel(team, compact = false) {
  const finish = team?.seasonFinish;

  if (!finish && !team?.seasonFinishDisplay && !team?.seasonFinishLabel) {
    return 'Unavailable';
  }

  if (compact) {
    return (
      finish?.label ||
      team?.seasonFinishLabel ||
      finish?.display ||
      team?.seasonFinishDisplay ||
      'Unavailable'
    );
  }

  return (
    finish?.display ||
    team?.seasonFinishDisplay ||
    finish?.label ||
    team?.seasonFinishLabel ||
    'Unavailable'
  );
}

function getSeasonFinishDetail(team) {
  const finish = team?.seasonFinish;

  if (!finish) {
    return '';
  }

  const parts = [];

  if (finish.roundReached) {
    parts.push(finish.roundReached);
  }

  if (finish.opponentName) {
    parts.push(
      finish.result === 'win'
        ? `defeated ${finish.opponentName}`
        : `vs. ${finish.opponentName}`
    );
  }

  return parts.join(' · ');
}

export function formatMetadataDate(value) {
  const text = String(value || '').trim();

  if (!text) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    const instant = new Date(text);
    return Number.isFinite(instant.getTime()) ? new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(instant) : '';
  }

  const throughMatch = text.match(/^Through(?:\s+games?)?\s+(.+)$/i);

  if (throughMatch?.[1]) {
    return throughMatch[1].split(/\s+-\s+Through(?:\s+games?)?/i)[0].trim();
  }

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(`${year}-${month}-${day}T12:00:00Z`);

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  return text;
}

function normalizeSeason(value, fallback = DEFAULT_TEAM_SEASON) {
  const season = Number(value);
  const fallbackSeason = Number(fallback);
  const safeFallback = FALLBACK_SEASONS.includes(fallbackSeason)
    ? fallbackSeason
    : DEFAULT_TEAM_SEASON;

  return Number.isInteger(season) && FALLBACK_SEASONS.includes(season)
    ? season
    : safeFallback;
}

function readTeamUrlState() {
  if (typeof window === 'undefined') {
    return { slug: '', season: DEFAULT_TEAM_SEASON };
  }

  const params = new URLSearchParams(window.location.search);

  return {
    slug: String(params.get('team') || '').trim().toLowerCase(),
    season: normalizeSeason(params.get('teamSeason')),
  };
}

function writeTeamUrl({
  slug = '',
  season = DEFAULT_TEAM_SEASON,
  mode = 'replace',
}) {
  const url = new URL(window.location.href);
  url.searchParams.delete('sport');
  url.searchParams.set('teamSeason', String(season));

  if (slug) {
    url.searchParams.set('team', slug);
  } else {
    url.searchParams.delete('team');
  }

  url.hash = 'teams';
  window.history[mode === 'push' ? 'pushState' : 'replaceState'](
    {},
    '',
    url.toString()
  );
}

function scrollToTeamsTop() {
  const section = document.getElementById('teams');

  section?.scrollIntoView({
    block: 'start',
    behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth',
  });
}

function DirectoryLoading() {
  return (
    <div
      className="teams-grid teams-loading-grid"
      aria-label="Loading team directory"
      aria-live="polite"
    >
      {Array.from({ length: 12 }, (_, index) => (
        <article
          className="team-directory-card team-directory-skeleton"
          key={index}
          aria-hidden="true"
        >
          <span className="team-directory-skeleton-logo" />
          <div>
            <span />
            <span />
          </div>
          <span />
        </article>
      ))}
    </div>
  );
}

function DirectoryError({ error, onRetry }) {
  return (
    <div className="teams-directory-state" role="alert">
      <span className="teams-directory-state-icon" aria-hidden="true">
        !
      </span>
      <h2>We couldn’t load the team directory.</h2>
      <p>{error || 'The NCAA team data is temporarily unavailable.'}</p>
      <button type="button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function DirectoryEmpty({ filter, query, onClear }) {
  const favoritesEmpty = filter === 'favorites';

  return (
    <div className="teams-directory-state">
      <span className="teams-directory-state-icon" aria-hidden="true">
        {favoritesEmpty ? '★' : '0'}
      </span>
      <h2>
        {favoritesEmpty
          ? 'No favorite teams yet.'
          : 'No teams match these filters.'}
      </h2>
      <p>
        {favoritesEmpty
          ? 'Select the star on any team to add it to My Teams.'
          : query
            ? 'Check the team name or clear the search field.'
            : 'Try another filter or sorting option.'}
      </p>
      <button type="button" onClick={onClear}>
        Clear filters
      </button>
    </div>
  );
}

function SeasonSelector({
  season,
  availableSeasons,
  onChange,
  disabled = false,
  compact = false,
}) {
  return (
    <label className={`teams-season-picker${compact ? ' compact' : ''}`}>
      <span>Season</span>
      <select
        value={season}
        disabled={disabled}
        aria-label="Team season"
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {availableSeasons.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function getFinalRankDisplay(team) {
  const rank = getFinalRank(team);

  if (!Number.isFinite(rank)) {
    return '';
  }

  const label = String(
    team?.finalRankLabel ||
      team?.archivedRankLabel ||
      team?.rankLabel ||
      rank
  ).trim();

  if (/^t-/i.test(label)) {
    return label.toUpperCase();
  }

  return `#${label.replace(/^#/, '')}`;
}

function RankingBadge({ team, profile = false }) {
  const display = getFinalRankDisplay(team);

  if (!display) {
    return null;
  }

  return (
    <span
      className={`team-directory-rank${profile ? ' profile-rank' : ''}`}
      aria-label={`${team?.season === 2026 ? 'Current' : 'Final'} AVCA rank ${display}`}
    >
      <small>{team?.season === 2026
        ? /preseason/i.test(team?.rankingArchiveType || '') ? 'PRESEASON' : 'CURRENT'
        : team?.rankingContextSeason && team.rankingContextSeason !== team.season
        ? `${team.rankingContextSeason} FINAL`
        : 'FINAL'}</small>
      <strong>{display}</strong>
    </span>
  );
}

function TeamDirectoryCard({
  team,
  favorite,
  onToggleFavorite,
  onOpenTeam,
}) {
  return (
    <article
      className={`team-directory-card${favorite ? ' favorite' : ''}`}
    >
      <div className="team-directory-card-top">
        <div className="team-directory-logo">
          <TeamLogo team={team} />
        </div>
        <RankingBadge team={team} />
        <button
          type="button"
          className={`team-directory-favorite${favorite ? ' active' : ''}`}
          aria-pressed={favorite}
          aria-label={
            favorite
              ? `Remove ${team.name} from My Teams`
              : `Add ${team.name} to My Teams`
          }
          title={favorite ? 'Remove from My Teams' : 'Add to My Teams'}
          onClick={() => onToggleFavorite(team)}
        >
          <span aria-hidden="true">{favorite ? '★' : '☆'}</span>
        </button>
      </div>

      <div className="team-directory-card-copy">
        <h3>{team.name}</h3>
        {team.longName && team.longName !== team.name ? (
          <p>{team.longName}</p>
        ) : null}
      </div>

      <dl className="team-directory-card-stats refined-card-stats">
        <div>
          <dt>Record</dt>
          <dd>{getTeamRecordLabel(team)}</dd>
        </div>
        <div>
          <dt>{team?.season === 2026
            ? 'Current AVCA'
            : team?.rankingContextSeason && team.rankingContextSeason !== team.season
            ? `${team.rankingContextSeason} final AVCA`
            : 'Final AVCA'}</dt>
          <dd>{getFinalRankDisplay(team) || 'Not ranked'}</dd>
        </div>
        {team?.season !== 2026 || !/^(Season in progress|Unavailable|—)$/i.test(getSeasonFinishLabel(team, true)) ? <div className="team-directory-finish-stat">
          <dt>NCAA tournament finish</dt>
          <dd>{getSeasonFinishLabel(team, true)}</dd>
        </div> : null}
      </dl>

      <button
        type="button"
        className="team-directory-open"
        aria-label={`Open ${team.name} team profile`}
        onClick={() => onOpenTeam(team)}
      >
        <span>Team profile</span>
        <span aria-hidden="true">→</span>
      </button>
    </article>
  );
}

function ProfileMetric({ label, value, detail = '', tone = '' }) {
  return (
    <div className={`team-profile-metric${tone ? ` ${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value || '—'}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function formatRecentMatchDate(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return text;
  }

  const [, year, month, day] = match;
  const date = new Date(`${year}-${month}-${day}T12:00:00Z`);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function RecentMatchesPanel({
  games,
  loading,
  error,
  dataUpdated,
  onOpenMatch,
}) {
  return (
    <section className="team-recent-matches" aria-labelledby="recent-matches-title">
      <div className="team-recent-matches-heading">
        <div>
          <p className="eyebrow">Season close</p>
          <h2 id="recent-matches-title">Last 3 matches</h2>
        </div>
        {dataUpdated ? (
          <small>Through {formatMetadataDate(dataUpdated)}</small>
        ) : null}
      </div>

      {loading ? (
        <div className="team-recent-matches-loading" aria-live="polite">
          Loading recent matches…
        </div>
      ) : error ? (
        <div className="team-recent-matches-empty" role="status">
          {error}
        </div>
      ) : games.length ? (
        <div className="team-recent-match-list">
          {games.map((game) => {
            const perspective = game.teamPerspective || {};
            const scoreAvailable =
              Number.isInteger(perspective.score) &&
              Number.isInteger(perspective.opponentScore);

            return (
              <button
                type="button"
                className="team-recent-match"
                key={game.gameId || `${game.date}-${perspective.opponentName}`}
                onClick={() => onOpenMatch(game)}
                aria-label={`Open ${formatRecentMatchDate(game.date)} match against ${
                  perspective.opponentName || 'opponent'
                } in Match Center`}
              >
                <span
                  className={`team-recent-match-result ${
                    perspective.result === 'win' ? 'win' : 'loss'
                  }`}
                  aria-label={perspective.result === 'win' ? 'Win' : 'Loss'}
                >
                  {perspective.result === 'win' ? 'W' : 'L'}
                </span>
                <span className="team-recent-match-copy">
                  <strong>{perspective.opponentName || 'Opponent unavailable'}</strong>
                  <small>{formatRecentMatchDate(game.date)}</small>
                </span>
                <span className="team-recent-match-score">
                  {scoreAvailable
                    ? `${perspective.score}–${perspective.opponentScore}`
                    : 'Final'}
                </span>
                <span className="team-recent-match-arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="team-recent-matches-empty" role="status">
          Recent match results are unavailable for this team.
        </div>
      )}
    </section>
  );
}

function TeamProfileToolbar({
  season,
  availableSeasons,
  loading = false,
  onBack,
  onSeasonChange,
}) {
  return (
    <div className="team-profile-toolbar">
      <button type="button" className="team-profile-back" onClick={onBack}>
        <span aria-hidden="true">←</span> All teams
      </button>
      <SeasonSelector
        season={season}
        availableSeasons={availableSeasons}
        disabled={loading}
        compact
        onChange={onSeasonChange}
      />
    </div>
  );
}

function TeamProfile({
  team,
  season,
  favorite,
  finalRankingsAvailable,
  recordsUpdated,
  finishesUpdated,
  rankingsUpdated,
  availableSeasons,
  recentMatches,
  recentMatchesLoading,
  recentMatchesError,
  onBack,
  onSeasonChange,
  onToggleFavorite,
  onOpenRecentMatch,
}) {
  const ranked = isFinalRanked(team);
  const rankingValue = finalRankingsAvailable
    ? ranked
      ? getFinalRankDisplay(team)
      : 'Not ranked'
    : 'Unavailable';
  const rankingDetail = ranked
    ? team.rankingObservedDate
      ? `Recorded ${formatMetadataDate(team.rankingObservedDate)}`
      : 'Final NCAA-recorded position'
    : finalRankingsAvailable
      ? 'No final Top 25 position'
      : '';
  const throughDate = team.finalRecord?.throughDate
    ? formatMetadataDate(team.finalRecord.throughDate)
    : '';
  const recordDetail = [
    throughDate ? `Through ${throughDate}` : '',
    team.winPctLabel ? `${team.winPctLabel} win pct` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const finishValue = getSeasonFinishLabel(team);
  const finishDetail = getSeasonFinishDetail(team);
  const dataUpdated = finishesUpdated || recordsUpdated || rankingsUpdated;

  return (
    <div className="team-profile">
      <TeamProfileToolbar
        season={season}
        availableSeasons={availableSeasons}
        onBack={onBack}
        onSeasonChange={onSeasonChange}
      />

      <div className="team-profile-hero">
        {ranked ? <RankingBadge team={team} profile /> : null}
        <div className="team-profile-logo">
          <TeamLogo team={team} variant="match-center" />
        </div>
        <div className="team-profile-heading">
          <p className="eyebrow coral">Women’s Division I</p>
          <h1>{team.name}</h1>
          {team.longName && team.longName !== team.name ? (
            <p>{team.longName}</p>
          ) : null}
        </div>
        <button
          type="button"
          className={`team-profile-favorite${favorite ? ' active' : ''}`}
          aria-pressed={favorite}
          aria-label={
            favorite
              ? `Remove ${team.name} from My Teams`
              : `Add ${team.name} to My Teams`
          }
          onClick={() => onToggleFavorite(team)}
        >
          <span aria-hidden="true">★</span>
          <span className="team-profile-favorite-label">
            {favorite ? 'Saved' : 'My Team'}
          </span>
        </button>
      </div>

      <div className="team-profile-metrics compact-profile-metrics">
        <ProfileMetric
          label="Season record"
          value={getTeamRecordLabel(team)}
          detail={recordDetail}
          tone="record-metric"
        />
        <ProfileMetric
          label="Final AVCA rank"
          value={rankingValue}
          detail={rankingDetail}
          tone="ranking-metric"
        />
        <ProfileMetric
          label="NCAA tournament finish"
          value={finishValue}
          detail={finishDetail}
          tone="tournament-finish-metric"
        />
      </div>

      <RecentMatchesPanel
        games={recentMatches}
        loading={recentMatchesLoading}
        error={recentMatchesError}
        dataUpdated={dataUpdated}
        onOpenMatch={onOpenRecentMatch}
      />
    </div>
  );
}

export default function TeamsDirectory() {
  const initialUrlState = useMemo(readTeamUrlState, []);
  const [selectedSeason, setSelectedSeason] = useState(initialUrlState.season);
  const [selectedSlug, setSelectedSlug] = useState(initialUrlState.slug);
  const [directory, setDirectory] = useState({
    loading: true,
    teams: [],
    error: '',
    partial: false,
    upstreamPartial: false,
    updated: '',
    rankingsUpdated: '',
    recordsUpdated: '',
    finishesUpdated: '',
    rankingsAvailable: false,
    finalRankingsAvailable: false,
    recordsAvailable: false,
    seasonFinishesAvailable: false,
    archiveComplete: false,
    divisionOneDirectoryVerified: false,
    directoryScope: 'archive-participants',
    dataStatus: null,
    season: initialUrlState.season,
    availableSeasons: FALLBACK_SEASONS,
  });
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_TEAMS);
  const [favorites, setFavorites] = useState(readFavoriteTeams);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const profileHistoryRef = useRef(false);

  const loadDirectory = useCallback(({ silent = false } = {}) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!silent) {
      setDirectory((current) => ({
        ...current,
        loading: true,
        error: '',
        season: selectedSeason,
        teams: current.season === selectedSeason ? current.teams : [],
      }));
    }

    getTeams({ season: selectedSeason, signal: controller.signal })
      .then((payload) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        const availableSeasons = payload.availableSeasons?.length
          ? Array.from(new Set(payload.availableSeasons)).sort(
              (left, right) => right - left
            )
          : FALLBACK_SEASONS;

        setDirectory({
          loading: false,
          teams: payload.teams || [],
          error: '',
          partial: Boolean(payload.partial),
          upstreamPartial: Boolean(payload.upstreamPartial),
          updated: payload.updated || '',
          rankingsUpdated: payload.rankingsUpdated || '',
          recordsUpdated: payload.recordsUpdated || '',
          finishesUpdated: payload.finishesUpdated || '',
          rankingsAvailable: Boolean(payload.rankingsAvailable),
          finalRankingsAvailable: Boolean(payload.finalRankingsAvailable),
          recordsAvailable: Boolean(payload.recordsAvailable),
          seasonFinishesAvailable: Boolean(payload.seasonFinishesAvailable),
          archiveComplete: Boolean(payload.archiveComplete),
          divisionOneDirectoryVerified: Boolean(
            payload.divisionOneDirectoryVerified
          ),
          directoryScope: payload.directoryScope || 'archive-participants',
          dataStatus: payload.dataStatus || null,
          season: payload.season || selectedSeason,
          availableSeasons,
        });
      })
      .catch((error) => {
        if (
          error?.name === 'AbortError' ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        if (silent) return;

        setDirectory((current) => ({
          ...current,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Unable to load the team directory.',
        }));
      });
  }, [selectedSeason]);

  useEffect(() => {
    loadDirectory();
    return () => controllerRef.current?.abort();
  }, [loadDirectory]);

  useEffect(() => {
    if (selectedSeason !== 2026) return undefined;

    const refresh = () => {
      if (document.visibilityState === 'visible') loadDirectory({ silent: true });
    };
    const interval = window.setInterval(refresh, 2 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadDirectory, selectedSeason]);

  useEffect(() => {
    function syncFavorites() {
      setFavorites(readFavoriteTeams());
    }

    window.addEventListener('storage', syncFavorites);
    window.addEventListener(FAVORITES_EVENT, syncFavorites);

    return () => {
      window.removeEventListener('storage', syncFavorites);
      window.removeEventListener(FAVORITES_EVENT, syncFavorites);
    };
  }, []);

  useEffect(() => {
    function handlePopState() {
      const next = readTeamUrlState();
      profileHistoryRef.current = false;
      setSelectedSlug(next.slug);
      setSelectedSeason(next.season);
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_TEAMS);
  }, [query, filter, selectedSeason]);

  useEffect(() => {
    if (directory.loading || directory.finalRankingsAvailable) {
      return;
    }

    if (filter === 'ranked') {
      setFilter('all');
    }

  }, [
    directory.loading,
    directory.finalRankingsAvailable,
    filter,
  ]);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);
  const teamsWithFavorites = useMemo(
    () =>
      directory.teams.map((team) => ({
        team,
        favorite: isFavoriteTeam({ team, favorites: favoriteSet }),
      })),
    [directory.teams, favoriteSet]
  );
  const favoriteCount = useMemo(
    () => teamsWithFavorites.filter((entry) => entry.favorite).length,
    [teamsWithFavorites]
  );
  const rankedCount = useMemo(
    () => directory.teams.filter(isFinalRanked).length,
    [directory.teams]
  );

  const filteredTeams = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(query);

    return teamsWithFavorites
      .filter(({ team, favorite }) => {
        if (filter === 'favorites' && !favorite) {
          return false;
        }

        if (filter === 'ranked' && !isFinalRanked(team)) {
          return false;
        }

        return matchesTeamSearch(team, normalizedQuery);
      })
      .sort((left, right) => {
        return compareTeamNames(left.team, right.team);
      });
  }, [teamsWithFavorites, query, filter]);

  const visibleTeams = filteredTeams.slice(0, visibleCount);
  const selectedTeam = useMemo(
    () =>
      directory.teams.find((team) => team.slug === selectedSlug) || null,
    [directory.teams, selectedSlug]
  );
  const selectedFavorite = selectedTeam
    ? isFavoriteTeam({ team: selectedTeam, favorites: favoriteSet })
    : false;


  function toggleFavorite(team) {
    const keys = getTeamFavoriteKeys(team);
    const canonicalKey = getFavoriteKey(team);
    const currentSet = new Set(favorites);
    const currentlyFavorite = keys.some((key) => currentSet.has(key));

    keys.forEach((key) => currentSet.delete(key));

    if (!currentlyFavorite && canonicalKey) {
      currentSet.add(canonicalKey);
    }

    const next = Array.from(currentSet);
    setFavorites(next);
    saveFavoriteTeams(next);
  }

  function changeSeason(nextSeason) {
    const normalized = normalizeSeason(nextSeason, selectedSeason);

    if (normalized === selectedSeason) {
      return;
    }

    if (filter === 'ranked') {
      setFilter('all');
    }

    setSelectedSeason(normalized);
    writeTeamUrl({
      slug: selectedSlug,
      season: normalized,
      mode: 'replace',
    });
  }

  function openTeam(team) {
    if (!team?.slug) {
      return;
    }

    profileHistoryRef.current = true;
    setSelectedSlug(team.slug);
    writeTeamUrl({
      slug: team.slug,
      season: selectedSeason,
      mode: 'push',
    });
    window.requestAnimationFrame(scrollToTeamsTop);
  }

  function closeTeam() {
    setSelectedSlug('');

    if (profileHistoryRef.current) {
      profileHistoryRef.current = false;
      window.history.back();
      return;
    }

    writeTeamUrl({ slug: '', season: selectedSeason, mode: 'replace' });
    window.requestAnimationFrame(scrollToTeamsTop);
  }

  function openRecentMatch(game) {
    const matchId = String(game?.gameId || game?.id || '').trim();
    const matchDate = String(game?.date || '').trim();

    if (!matchId || !/^\d{4}-\d{2}-\d{2}$/.test(matchDate)) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('date', matchDate);
    url.searchParams.delete('sport');
    url.searchParams.set('match', matchId);
    url.searchParams.delete('team');
    url.hash = 'scores';

    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(
      typeof PopStateEvent === 'function'
        ? new PopStateEvent('popstate')
        : new Event('popstate')
    );
  }

  function clearFilters() {
    setQuery('');
    setFilter('all');
  }

  if (selectedSlug) {
    return (
      <section className="teams-section" id="teams">
        <div className="shell">
          {directory.loading ? (
            <div className="team-profile">
              <TeamProfileToolbar
                season={selectedSeason}
                availableSeasons={directory.availableSeasons}
                loading
                onBack={closeTeam}
                onSeasonChange={changeSeason}
              />
              <div
                className="teams-directory-state team-profile-loading"
                aria-live="polite"
              >
                <span className="teams-directory-state-icon" aria-hidden="true">
                  V
                </span>
                <h2>Loading {selectedSeason} team profile…</h2>
              </div>
            </div>
          ) : directory.error ? (
            <div className="team-profile">
              <TeamProfileToolbar
                season={selectedSeason}
                availableSeasons={directory.availableSeasons}
                onBack={closeTeam}
                onSeasonChange={changeSeason}
              />
              <DirectoryError error={directory.error} onRetry={loadDirectory} />
            </div>
          ) : selectedTeam ? (
            <TeamProfileHub
              team={selectedTeam}
              season={selectedSeason}
              favorite={selectedFavorite}
              finalRankingsAvailable={directory.finalRankingsAvailable}
              rankingsUpdated={directory.rankingsUpdated}
              recordsUpdated={directory.recordsUpdated}
              finishesUpdated={directory.finishesUpdated}
              availableSeasons={directory.availableSeasons}
              onBack={closeTeam}
              onSeasonChange={changeSeason}
              onToggleFavorite={toggleFavorite}
            />
          ) : (
            <div className="team-profile">
              <TeamProfileToolbar
                season={selectedSeason}
                availableSeasons={directory.availableSeasons}
                onBack={closeTeam}
                onSeasonChange={changeSeason}
              />
              <div className="teams-directory-state">
                <span className="teams-directory-state-icon" aria-hidden="true">
                  ?
                </span>
                <h2>Team profile not found.</h2>
                <p>
                  This program may not have Division I data available for the
                  selected season.
                </p>
                <button type="button" onClick={closeTeam}>
                  Return to all teams
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="teams-section" id="teams">
      <div className="shell">
        <div className="teams-directory-heading compact-directory-heading">
          <div>
            <p className="eyebrow coral">Women’s Division I</p>
            <h1>Team Directory</h1>
          </div>
          <div className="teams-directory-heading-meta">
            <div className="teams-directory-count">
              <strong>
                {directory.loading
                  ? 'Loading…'
                  : `${directory.teams.length} teams`}
              </strong>
            </div>
            <SeasonSelector
              season={selectedSeason}
              availableSeasons={directory.availableSeasons}
              disabled={directory.loading}
              compact
              onChange={changeSeason}
            />
          </div>
        </div>

        <div className="teams-directory-controls">
          <label className="teams-directory-search">
            <span>Search teams</span>
            <div>
              <span aria-hidden="true">⌕</span>
              <input
                id="team-directory-search-input"
                type="search"
                value={query}
                placeholder="Search by team, finish, or rank"
                autoComplete="off"
                enterKeyHint="search"
                onChange={(event) => setQuery(event.target.value)}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear team search"
                  onClick={() => setQuery('')}
                >
                  ×
                </button>
              ) : null}
            </div>
          </label>

          <div
            className="teams-directory-filter-group"
            aria-label="Filter teams"
          >
            {FILTER_OPTIONS.map((option) => {
              const disabled =
                Boolean(option.requiresRankings) &&
                !directory.finalRankingsAvailable;
              const count =
                option.value === 'favorites'
                  ? favoriteCount
                  : option.value === 'ranked'
                    ? rankedCount
                    : directory.teams.length;

              return (
                <button
                  type="button"
                  key={option.value}
                  className={filter === option.value ? 'active' : ''}
                  disabled={disabled}
                  aria-pressed={filter === option.value}
                  title={
                    disabled
                      ? `AVCA rankings are unavailable for ${selectedSeason}.`
                      : undefined
                  }
                  onClick={() => setFilter(option.value)}
                >
                  <span>{option.label}</span>
                  <strong>{count}</strong>
                </button>
              );
            })}
          </div>

        </div>

        <div className="teams-directory-summary compact-directory-summary">
          <span>
            {directory.loading
              ? `Loading ${selectedSeason} teams…`
              : `${filteredTeams.length} ${
                  filteredTeams.length === 1 ? 'team' : 'teams'
                } shown`}
          </span>
          {directory.partial ? (
            <strong role="status">Some fields are temporarily incomplete.</strong>
          ) : null}
          {directory.updated || directory.recordsUpdated ? (
            <small>{selectedSeason === 2026 && directory.recordsUpdated ? `Record source updated ${formatMetadataDate(directory.recordsUpdated)}` : `Source published ${formatMetadataDate(directory.updated)}`}</small>
          ) : null}
        </div>

        {directory.loading ? (
          <DirectoryLoading />
        ) : directory.error ? (
          <DirectoryError error={directory.error} onRetry={loadDirectory} />
        ) : visibleTeams.length ? (
          <>
            <div className="teams-grid">
              {visibleTeams.map(({ team, favorite }) => (
                <TeamDirectoryCard
                  key={team.slug || team.name}
                  team={team}
                  favorite={favorite}
                  onToggleFavorite={toggleFavorite}
                  onOpenTeam={openTeam}
                />
              ))}
            </div>
            {visibleCount < filteredTeams.length ? (
              <div className="teams-load-more">
                <button
                  type="button"
                  onClick={() =>
                    setVisibleCount((current) => current + LOAD_MORE_COUNT)
                  }
                >
                  Load more teams
                </button>
                <small>
                  Showing {visibleTeams.length} of {filteredTeams.length}
                </small>
              </div>
            ) : null}
          </>
        ) : (
          <DirectoryEmpty
            filter={filter}
            query={query}
            onClear={clearFilters}
          />
        )}
      </div>
    </section>
  );
}
